export default function Hero() {
  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-[600px] md:min-h-[700px] flex items-center pt-[72px] overflow-hidden bg-background-50"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent-100/30 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4"></div>
      </div>

      <div className="relative w-full max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          <div className="flex-1 w-full text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100/60 text-accent-700 text-xs font-semibold mb-5 tracking-wide">
              <i className="ri-moon-clear-fill text-sm"></i>
              <span>深夜影院</span>
              <span className="w-1 h-1 rounded-full bg-accent-400"></span>
              <span>免费高清</span>
              <span className="w-1 h-1 rounded-full bg-accent-400"></span>
              <span>秒播不卡</span>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-3">
              夜猫电影院
              <span className="block text-primary-600 text-2xl md:text-3xl lg:text-4xl mt-1">
                深夜追剧不缓冲的高清在线影院
              </span>
            </h1>

            <p className="font-heading text-lg md:text-xl text-foreground-600 italic mb-5">
              专为夜猫子打造的午夜剧场
            </p>

            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-[580px] mx-auto lg:mx-0 mb-6">
              夜猫电影院汇集海量高清电影、电视剧资源，覆盖动作、喜剧、悬疑、科幻、爱情等全类型片库。每日凌晨更新最新剧集，采用分布式加速节点确保夜间高峰时段也能流畅播放，告别缓冲等待。无论你是深夜追剧党还是午夜电影爱好者，这里都是你的专属观影角落。当前热播《暗夜追踪》《星海迷途》《午夜食堂》等话题新片持续更新中。
            </p>

            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 md:gap-5 mb-7">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-primary-50 rounded-lg">
                <i className="ri-fire-line text-primary-500 text-sm"></i>
                <span className="text-xs md:text-sm text-foreground-700 font-medium whitespace-nowrap">日更新50+部</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-accent-50 rounded-lg">
                <i className="ri-film-line text-accent-500 text-sm"></i>
                <span className="text-xs md:text-sm text-foreground-700 font-medium whitespace-nowrap">全类型覆盖20000+</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary-100 rounded-lg">
                <i className="ri-flashlight-line text-secondary-600 text-sm"></i>
                <span className="text-xs md:text-sm text-foreground-700 font-medium whitespace-nowrap">秒开零等待</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <a
                href="#cards"
                onClick={(e) => handleScroll(e, '#cards')}
                className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                立即观影
              </a>
              <a
                href="#index"
                onClick={(e) => handleScroll(e, '#index')}
                className="inline-flex items-center gap-2 px-7 py-3 border-2 border-secondary-300 text-foreground-700 font-semibold text-sm rounded-lg hover:border-secondary-500 hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap cursor-pointer bg-background-50"
              >
                <i className="ri-stack-line text-lg"></i>
                浏览专题
              </a>
            </div>
          </div>

          <div className="flex-1 w-full max-w-[500px] lg:max-w-none">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-br from-primary-200/40 via-accent-100/30 to-primary-100/50 rounded-2xl blur-xl"></div>
              <div className="relative bg-background-50 rounded-2xl overflow-hidden border border-background-200/60">
                <div className="aspect-[4/3] w-full relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20night%20scene%20with%20film%20projector%20casting%20golden%20light%20through%20movie%20reels%2C%20cozy%20dark%20home%20theater%20atmosphere%20with%20popcorn%20and%20soft%20ambient%20glow%2C%20vintage%20cinema%20aesthetic%20with%20warm%20amber%20and%20terracotta%20tones%2C%20artistic%20composition%20with%20shallow%20depth%20of%20field%2C%20elegant%20and%20inviting%20mood&width=800&height=600&seq=hero-cinema-01&orientation=landscape"
                    alt="夜猫电影院深夜观影氛围"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
                    <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1.5">
                      <i className="ri-live-line text-primary-300 text-xs"></i>
                      <span className="text-background-50 text-xs font-medium">正在热播</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1.5">
                      <i className="ri-star-fill text-primary-300 text-xs"></i>
                      <span className="text-background-50 text-xs font-medium">4.9 好评</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}